/**
 * PDF Export Utility
 * 
 * Exports project specifications to PDF format with proper styling
 */

import jsPDF from 'jspdf';

export interface ProjectSpecification {
  projectName: string;
  clientName: string;
  constitution?: string;
  specification?: string;
  technicalPlan?: string;
  tasks?: string;
  generatedDate?: string;
}

/**
 * Export project specifications to PDF
 */
export const exportSpecificationsToPDF = (spec: ProjectSpecification): void => {
  try {
    // Create new PDF document (A4 size)
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = margin;

    // Helper function to add new page if needed
    const checkPageBreak = (requiredSpace: number = 10) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Helper function to add text with word wrap
    const addWrappedText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      
      const lines = doc.splitTextToSize(text, maxWidth);
      const lineHeight = fontSize * 0.5;
      
      for (let i = 0; i < lines.length; i++) {
        checkPageBreak(lineHeight);
        doc.text(lines[i], margin, yPosition);
        yPosition += lineHeight;
      }
      
      yPosition += 3; // Add spacing after text block
    };

    // Helper function to add section
    const addSection = (title: string, content: string | undefined) => {
      if (!content) return;

      checkPageBreak(20);
      
      // Add section title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(33, 150, 243); // Blue color
      doc.text(title, margin, yPosition);
      yPosition += 7;

      // Add horizontal line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 5;

      // Reset text color
      doc.setTextColor(0, 0, 0);

      // Add content
      const paragraphs = content.split('\n\n');
      paragraphs.forEach(paragraph => {
        if (paragraph.trim()) {
          addWrappedText(paragraph.trim(), 10, false);
          yPosition += 2; // Extra spacing between paragraphs
        }
      });

      yPosition += 5; // Extra spacing after section
    };

    // Cover Page
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(33, 150, 243);
    doc.text('Project Specifications', pageWidth / 2, 40, { align: 'center' });

    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text(spec.projectName, pageWidth / 2, 55, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Client: ${spec.clientName}`, pageWidth / 2, 65, { align: 'center' });

    if (spec.generatedDate) {
      doc.text(`Generated: ${spec.generatedDate}`, pageWidth / 2, 72, { align: 'center' });
    }

    // Add VegaDigital branding
    doc.setFontSize(10);
    doc.text('VegaDigital Solutions Platform', pageWidth / 2, pageHeight - 15, { align: 'center' });

    // Start content on new page
    doc.addPage();
    yPosition = margin;

    // Table of Contents
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(33, 150, 243);
    doc.text('Table of Contents', margin, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    let pageNumber = 3;
    if (spec.constitution) {
      doc.text(`1. Project Constitution`, margin + 5, yPosition);
      doc.text(`${pageNumber}`, pageWidth - margin - 10, yPosition, { align: 'right' });
      yPosition += 7;
      pageNumber++;
    }
    if (spec.specification) {
      doc.text(`2. Technical Specification`, margin + 5, yPosition);
      doc.text(`${pageNumber}`, pageWidth - margin - 10, yPosition, { align: 'right' });
      yPosition += 7;
      pageNumber++;
    }
    if (spec.technicalPlan) {
      doc.text(`3. Implementation Plan`, margin + 5, yPosition);
      doc.text(`${pageNumber}`, pageWidth - margin - 10, yPosition, { align: 'right' });
      yPosition += 7;
      pageNumber++;
    }
    if (spec.tasks) {
      doc.text(`4. Task Breakdown`, margin + 5, yPosition);
      doc.text(`${pageNumber}`, pageWidth - margin - 10, yPosition, { align: 'right' });
      yPosition += 7;
    }

    // Add sections
    if (spec.constitution) {
      doc.addPage();
      yPosition = margin;
      addSection('📜 Project Constitution', spec.constitution);
    }

    if (spec.specification) {
      doc.addPage();
      yPosition = margin;
      addSection('🏗️ Technical Specification', spec.specification);
    }

    if (spec.technicalPlan) {
      doc.addPage();
      yPosition = margin;
      addSection('💻 Implementation Plan', spec.technicalPlan);
    }

    if (spec.tasks) {
      doc.addPage();
      yPosition = margin;
      addSection('✅ Task Breakdown', spec.tasks);
    }

    // Add page numbers to all pages except cover
    const totalPages = doc.internal.pages.length - 1; // Exclude the initial empty page
    for (let i = 2; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i - 1} of ${totalPages - 1}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Generate filename
    const filename = `${spec.projectName.replace(/[^a-z0-9]/gi, '_')}_Specifications.pdf`;

    // Save the PDF
    doc.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};

/**
 * Export single specification section to PDF
 */
export const exportSingleSectionToPDF = (
  projectName: string,
  clientName: string,
  sectionTitle: string,
  content: string
): void => {
  const spec: ProjectSpecification = {
    projectName,
    clientName,
    generatedDate: new Date().toLocaleDateString(),
  };

  // Map section title to specification field
  const titleMap: Record<string, keyof ProjectSpecification> = {
    'Constitution': 'constitution',
    'Specification': 'specification',
    'Technical Plan': 'technicalPlan',
    'Implementation Plan': 'technicalPlan',
    'Tasks': 'tasks',
    'Task Breakdown': 'tasks',
  };

  const fieldName = titleMap[sectionTitle];
  if (fieldName) {
    spec[fieldName] = content;
  }

  exportSpecificationsToPDF(spec);
};
